import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Channel,
  ChannelDetail,
  ChannelListQuery,
  ConnectWhatsAppInput,
  EmbeddedSignupInput,
  UpdateChannelInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['channels'] as const;

function toQuery(q: ChannelListQuery): Record<string, string | undefined> {
  return {
    type: q.type,
    includeDisconnected: q.includeDisconnected ? 'true' : undefined,
  };
}

export const listChannels = (q: ChannelListQuery = {}) =>
  apiClient.get<Channel[]>('/channels', { query: toQuery(q) });
export const getChannel = (id: string) => apiClient.get<ChannelDetail>(`/channels/${id}`);
export const connectWhatsapp = (input: ConnectWhatsAppInput) =>
  apiClient.post<ChannelDetail>('/channels/whatsapp/connect', input);
export const connectWhatsappEmbeddedSignup = (input: EmbeddedSignupInput) =>
  apiClient.post<ChannelDetail>('/channels/whatsapp/embedded-signup', input);
export const reauthorizeWhatsapp = (channelId: string, input: EmbeddedSignupInput) =>
  apiClient.post<ChannelDetail>(`/channels/whatsapp/${channelId}/reauthorize`, input);
export const updateWhatsappToken = (channelId: string, accessToken: string) =>
  apiClient.post<ChannelDetail>(`/channels/whatsapp/${channelId}/token`, { accessToken });
export const subscribeWhatsappWebhooks = (channelId: string) =>
  apiClient.post<ChannelDetail>(`/channels/whatsapp/${channelId}/subscribe-webhooks`);
export const refreshSendingHealth = (channelId: string) =>
  apiClient.post<ChannelDetail>(`/channels/whatsapp/${channelId}/sending-health/refresh`);
export const disconnectChannel = (id: string) => apiClient.delete<void>(`/channels/${id}`);
export const updateChannel = (id: string, patch: UpdateChannelInput) =>
  apiClient.patch<ChannelDetail>(`/channels/${id}`, patch);

export function useChannels(query: ChannelListQuery = {}) {
  return useQuery<Channel[], ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listChannels(query),
  });
}

export function useChannel(id: string | undefined) {
  return useQuery<ChannelDetail, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getChannel(id!),
    enabled: Boolean(id),
  });
}

export function useConnectWhatsapp() {
  const qc = useQueryClient();
  return useMutation<ChannelDetail, ApiClientError, ConnectWhatsAppInput>({
    mutationFn: connectWhatsapp,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useConnectWhatsappEmbeddedSignup() {
  const qc = useQueryClient();
  return useMutation<ChannelDetail, ApiClientError, EmbeddedSignupInput>({
    mutationFn: connectWhatsappEmbeddedSignup,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useReauthorizeWhatsapp(channelId: string) {
  const qc = useQueryClient();
  return useMutation<ChannelDetail, ApiClientError, EmbeddedSignupInput>({
    mutationFn: (input) => reauthorizeWhatsapp(channelId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateWhatsappToken(channelId: string) {
  const qc = useQueryClient();
  return useMutation<ChannelDetail, ApiClientError, string>({
    mutationFn: (accessToken) => updateWhatsappToken(channelId, accessToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSubscribeWhatsappWebhooks(channelId: string) {
  const qc = useQueryClient();
  return useMutation<ChannelDetail, ApiClientError, void>({
    mutationFn: () => subscribeWhatsappWebhooks(channelId),
    // Refetch on failure too - the server records the FAILED outcome.
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDisconnectChannel() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: disconnectChannel,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateChannel(channelId: string) {
  const qc = useQueryClient();
  return useMutation<ChannelDetail, ApiClientError, UpdateChannelInput>({
    mutationFn: (patch) => updateChannel(channelId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Re-reads the channel's numbers' messaging limit + quality from Meta. */
export function useRefreshSendingHealth(channelId: string) {
  const qc = useQueryClient();
  return useMutation<ChannelDetail, ApiClientError, void>({
    mutationFn: () => refreshSendingHealth(channelId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
